import { z } from 'zod';
import { moneySchema } from '../money';

/**
 * Vorkalkulation eines Projekts (ADR 0016): Plan-Material je Hauptwarengruppe +
 * Plan-Stunden je Position. Wird als Ganzes ersetzt (Replace-Semantik) — der
 * Editor schickt den vollständigen Plan.
 */
export const putProjectPlanSchema = z.object({
  material: z
    .array(
      z.object({
        mainGroupCode: z.number().int().min(1000).max(9999),
        plannedAmount: moneySchema,
      }),
    )
    .max(100),
  labor: z
    .array(
      z.object({
        positionId: z.string().uuid(),
        plannedHours: z.number().min(0),
      }),
    )
    .max(100),
});
export type PutProjectPlanInput = z.infer<typeof putProjectPlanSchema>;

export const planMaterialRowSchema = z.object({
  mainGroupCode: z.number().int(),
  mainGroupName: z.string().nullable(),
  plannedAmount: moneySchema,
});
export type PlanMaterialRow = z.infer<typeof planMaterialRowSchema>;

export const planLaborRowSchema = z.object({
  positionId: z.string().uuid(),
  positionName: z.string(),
  plannedHours: z.number(),
  /** Aktueller Positions-Satz (für die Plan-Kosten-Vorschau). null, wenn Position fehlt. */
  hourlyRate: moneySchema.nullable(),
  plannedCost: moneySchema,
});
export type PlanLaborRow = z.infer<typeof planLaborRowSchema>;

export const projectPlanResponseSchema = z.object({
  projectId: z.string().uuid(),
  material: z.array(planMaterialRowSchema),
  labor: z.array(planLaborRowSchema),
});
export type ProjectPlanResponse = z.infer<typeof projectPlanResponseSchema>;

/**
 * Ergebnis der Ableitung „Vorkalkulation aus Beleg übernehmen" (ADR 0016): der
 * neue Plan plus eine Zusammenfassung übersprungener Auftragspositionen (Freitext,
 * Artikel ohne Warengruppe/EK) für die Nutzer-Rückmeldung.
 */
export const derivePlanSummarySchema = z.object({
  usedItems: z.number().int().min(0),
  skipped: z.object({
    freeText: z.number().int().min(0),
    withoutCommodityGroup: z.number().int().min(0),
    withoutPurchasePrice: z.number().int().min(0),
  }),
});
export type DerivePlanSummary = z.infer<typeof derivePlanSummarySchema>;

export const derivePlanResponseSchema = z.object({
  plan: projectPlanResponseSchema,
  summary: derivePlanSummarySchema,
});
export type DerivePlanResponse = z.infer<typeof derivePlanResponseSchema>;
